import { type FC, useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import { mdiClose, mdiMapMarkerOutline } from "@mdi/js";
import { Input, TextField } from "@heroui/react";
import type { HelpPoint } from "../types";
import { HelpLeafletMap } from "./HelpLeafletMap";
import {
  type AddressSuggestion,
  effectivePointCoords,
  fetchAddressSuggestions,
  formatPointAddress,
  parseAddressSuggestion,
  reverseGeocode,
} from "./help-location-map";

interface HelpPointLocationEditorProps {
  title: string;
  point: HelpPoint;
  onChange: (point: HelpPoint) => void;
  mapActive?: boolean;
  hideTitle?: boolean;
  className?: string;
}

const HelpPointLocationEditor: FC<HelpPointLocationEditorProps> = ({
  title,
  point,
  onChange,
  mapActive = true,
  hideTitle = false,
  className,
}) => {
  const [addressQuery, setAddressQuery] = useState(() =>
    formatPointAddress(point.city, point.address),
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [mapKey, setMapKey] = useState(0);
  const [pendingReverseAddress, setPendingReverseAddress] = useState<string | null>(null);
  const [pendingCoordUpdate, setPendingCoordUpdate] = useState<{ lat: number; lon: number } | null>(
    null,
  );
  const [markerWasMoved, setMarkerWasMoved] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const addressFocusedRef = useRef(false);
  const suggestRequestRef = useRef(0);
  const reverseRequestRef = useRef(0);

  useEffect(() => {
    if (addressFocusedRef.current) return;
    setAddressQuery(formatPointAddress(point.city, point.address));
    setPendingReverseAddress(null);
    setPendingCoordUpdate(null);
    setMarkerWasMoved(false);
  }, [point.city, point.address]);

  useEffect(() => {
    if (!addressFocusedRef.current) return;
    const query = addressQuery.trim();
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    const requestId = ++suggestRequestRef.current;
    const timer = window.setTimeout(() => {
      void fetchAddressSuggestions(query).then((next) => {
        if (suggestRequestRef.current !== requestId) return;
        setSuggestions(next);
        if (addressFocusedRef.current && next.length > 0) {
          setShowSuggestions(true);
        }
      });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [addressQuery]);

  const applyPointPatch = (patch: Partial<HelpPoint>) => {
    onChange({ ...point, ...patch });
  };

  const commitTypedAddress = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) {
      if (point.city || point.address) {
        applyPointPatch({ city: "", address: "" });
      }
      return;
    }
    const parsed = parseAddressSuggestion(trimmed);
    const nextCity = parsed.city;
    const nextAddress = parsed.address;
    if (nextCity === point.city && nextAddress === point.address) return;
    applyPointPatch({ city: nextCity, address: nextAddress });
  };

  const selectAddress = (suggestion: AddressSuggestion) => {
    const parsed = parseAddressSuggestion(suggestion.label);
    setAddressQuery(suggestion.label);
    setShowSuggestions(false);
    setPendingReverseAddress(null);

    if (markerWasMoved) {
      setPendingCoordUpdate({ lat: suggestion.lat, lon: suggestion.lon });
      applyPointPatch({ city: parsed.city, address: parsed.address });
    } else {
      applyPointPatch({
        city: parsed.city,
        address: parsed.address,
        lat: suggestion.lat,
        lon: suggestion.lon,
      });
      setMapKey((key) => key + 1);
    }
  };

  const applyCoordUpdate = () => {
    if (!pendingCoordUpdate) return;
    applyPointPatch({
      lat: pendingCoordUpdate.lat,
      lon: pendingCoordUpdate.lon,
    });
    setPendingCoordUpdate(null);
    setMarkerWasMoved(false);
    setMapKey((key) => key + 1);
  };

  const handleMapPositionChange = (lat: number, lon: number) => {
    const newLat = parseFloat(lat.toFixed(6));
    const newLon = parseFloat(lon.toFixed(6));
    applyPointPatch({ lat: newLat, lon: newLon });
    setMarkerWasMoved(true);
    setPendingCoordUpdate(null);
    const requestId = ++reverseRequestRef.current;
    void reverseGeocode(newLat, newLon).then((label) => {
      if (reverseRequestRef.current !== requestId) return;
      if (label) setPendingReverseAddress(label);
    });
  };

  const applyReverseAddress = () => {
    if (!pendingReverseAddress) return;
    const parsed = parseAddressSuggestion(pendingReverseAddress);
    setAddressQuery(
      pendingReverseAddress.includes("°")
        ? formatPointAddress(point.city, point.address)
        : pendingReverseAddress,
    );
    applyPointPatch({
      city: parsed.city || point.city,
      address: parsed.address || pendingReverseAddress,
    });
    setPendingReverseAddress(null);
  };

  useEffect(() => {
    const handler = (event: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const { lat, lon } = effectivePointCoords(point);

  return (
    <div
      className={className ? `help-edit-block ${className}` : "help-edit-block"}
      data-testid="help-point-location-editor"
    >
      {hideTitle ? null : <span className="help-view-section__title">{title}</span>}

      <label className="help-form-field help-form-field--wide">
        Адрес
        <div className="help-point-address-wrap" ref={suggestionsRef}>
          <TextField fullWidth>
            <Input
              placeholder="Начните вводить адрес..."
              value={addressQuery}
              onChange={(event) => {
                const next = event.target.value;
                setAddressQuery(next);
                setPendingReverseAddress(null);
                setPendingCoordUpdate(null);
                setShowSuggestions(true);
                commitTypedAddress(next);
              }}
              onFocus={() => {
                addressFocusedRef.current = true;
                if (suggestions.length > 0) setShowSuggestions(true);
              }}
              onBlur={() => {
                addressFocusedRef.current = false;
                commitTypedAddress(addressQuery);
              }}
            />
          </TextField>

          {pendingReverseAddress ? (
            <div className="help-point-address-hint">
              <Icon
                path={mdiMapMarkerOutline}
                size={0.55}
                className="help-point-address-hint__icon"
              />
              <span className="help-point-address-hint__text">
                Точка перемещена: <b>{pendingReverseAddress}</b>
              </span>
              <button
                type="button"
                className="help-point-address-hint__btn"
                onClick={applyReverseAddress}
              >
                Обновить адрес
              </button>
              <button
                type="button"
                className="help-point-address-hint__dismiss"
                onClick={() => setPendingReverseAddress(null)}
                aria-label="Закрыть"
              >
                <Icon path={mdiClose} size={0.5} />
              </button>
            </div>
          ) : null}

          {pendingCoordUpdate ? (
            <div className="help-point-address-hint help-point-address-hint--coords">
              <Icon
                path={mdiMapMarkerOutline}
                size={0.55}
                className="help-point-address-hint__icon"
              />
              <span className="help-point-address-hint__text">
                Выбран другой адрес. Переместить маркер к нему?
              </span>
              <button
                type="button"
                className="help-point-address-hint__btn"
                onClick={applyCoordUpdate}
              >
                Обновить точку
              </button>
              <button
                type="button"
                className="help-point-address-hint__dismiss"
                onClick={() => setPendingCoordUpdate(null)}
                aria-label="Закрыть"
              >
                <Icon path={mdiClose} size={0.5} />
              </button>
            </div>
          ) : null}

          {showSuggestions && suggestions.length > 0 ? (
            <div className="help-point-suggestions">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.label}
                  type="button"
                  className="help-point-suggestion"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectAddress(suggestion)}
                >
                  <Icon
                    path={mdiMapMarkerOutline}
                    size={0.65}
                    className="help-point-suggestion__icon"
                  />
                  <span className="help-point-suggestion__text">{suggestion.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </label>

      <div className="help-point-map-field">
        <span className="help-point-map-field__label">Расположение на карте</span>
        <p className="help-point-map-field__hint">
          Перетащите маркер для уточнения местоположения
        </p>
        {mapActive ? (
          <HelpLeafletMap
            key={mapKey}
            lat={lat}
            lon={lon}
            onPositionChange={handleMapPositionChange}
          />
        ) : null}
      </div>

      <label className="help-form-field help-form-field--wide">
        Комментарий к точке
        <TextField fullWidth>
          <Input
            value={point.note}
            onChange={(event) => applyPointPatch({ note: event.target.value })}
          />
        </TextField>
      </label>
    </div>
  );
};

export default HelpPointLocationEditor;
